import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {APP_URL, TIKTOK_APP_ID, LINKEDIN_AUTHORIZATION_URL} from '../../../settings/local_settings';
import {FACEBOOK_CHANNEL, GOOGLE_CHANNEL, LINKEDIN_CHANNEL, TIKTOK_CHANNEL} from '../../channels/channels';
import {LaterPlatformsCard} from './LaterPlatformsCard';
import {PlatformCard} from './PlatformCard';
import {fetchAuthUrlFor, fetchConnections} from '../api';
import {clearOauthReturn, setOauthReturn} from '../oauthReturn';
import {clearPendingLinkedinReconnect} from '../../agency/connections/pendingLinkedinReconnect';
import {forgetReconnect} from '../../agency/connections/reconnectNote';
import {accountCountsOf, canBuildPortfolio, credentialsOf, isClosedAccount, isCoveredAccount, PLATFORM_CHANNELS,
        resultClassName, resultHintKey, resultStateOf, resultTitleKey, showsClosedCount} from './platformsResult';
import {useOnboarding} from '../wizardContext';
import {CLARITY_UNMASK} from '../../../utils/clarity/unmask';
import {ScreenTracker} from '../../../utils/ScreenTracker';
import {isPhoneViewport} from '../mobileContinue';
import {CANCELLED, FACEBOOK_SDK_NOT_LOADED, NO_AUTH_URL, connectCancelEvent, connectErrorEvent, connectReturnEvent,
        connectTapEvents} from '../connectEvents';
import {isNetworkError} from '../../../utils/apiResponse';
import {MobileLaterNote} from './MobileLaterNote';

/**
 * Paso 2 — conectar las plataformas.
 *
 * Con UNA alcanza para seguir: la propuesta de clientes sale más completa cuantas más haya, pero
 * exigir todas antes de mostrar algo convierte el arranque en un trámite. Por eso el paso nunca
 * avanza solo: al terminar de descubrir cuentas ofrece las dos salidas —sumar otra plataforma o
 * armar la cartera con lo que hay— y el usuario elige.
 */

const POLL_MS = 8000;
const MAX_POLL_MS = 5 * 60 * 1000;

/**
 * Cada cuántas vueltas se vuelve a traer el padrón de cuentas.
 *
 * El contador de la tarjeta («Conectada · N cuentas») es lo único que le dice al usuario que algo
 * está pasando, y pidiendo sólo `connections` no se movía: saltaba de golpe al terminar el sync,
 * que es justo cuando ya no hace falta narrar nada.
 *
 * No se pide en CADA vuelta porque el padrón es la consulta cara del paso —así se llegó a 37
 * pedidos de la cartera entera sólo para contestar «¿ya terminó?»—. Una de cada tres da un número
 * que crece cada ~24 s, que es el ritmo al que una persona mira una pantalla de espera.
 */
const ACCOUNTS_EVERY = 3;

/** Ata el motivo del pie al botón que bloquea, para quien no ve los dos juntos en pantalla. */
const MOTIVO_BLOQUEO_ID = 'ob-plataformas-motivo-bloqueo';

const PLATFORMS = [
    {channel: GOOGLE_CHANNEL, hintKey: 'google'},
    {channel: FACEBOOK_CHANNEL, hintKey: 'meta'},
    {channel: TIKTOK_CHANNEL, hintKey: null},
    {channel: LINKEDIN_CHANNEL, hintKey: null},
];

export function StepPlatforms() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {server, loading, loadCandidates} = useOnboarding();
    const [connections, setConnections] = useState(null);
    const [failedChannel, setFailedChannel] = useState(null);
    // POR QUÉ falló, cuando el backend lo dice. Es un CÓDIGO (`GOOGLE_NO_ADS_ACCESS`…), no un
    // mensaje: la tarjeta tiene copy propio para cada uno. Sin esto, el caso más frecuente del paso
    // —la cuenta de Google con la que la persona entra no llega a ninguna cuenta de Google Ads—
    // llegaba como «suele ser un permiso que falta», que manda a pedir un permiso inexistente.
    const [failedReason, setFailedReason] = useState(null);
    // Meta autoriza sin salir de la página, así que su cancelación no vuelve por la URL como la de
    // los otros tres: la avisa la propia tarjeta.
    const [cancelledChannel, setCancelledChannel] = useState(null);
    const [gaveUp, setGaveUp] = useState(false);
    const pollStartedAt = useRef(null);
    const pollTimer = useRef(null);
    // El destino de «Conectar otra plataforma antes»: el grid, no el tope de la pagina.
    const platformsRef = useRef(null);

    // Qué pasó en la vuelta del OAuth lo dice la URL. Se lee una vez y se limpia: recargar la
    // pantalla no puede volver a anunciar un fallo que ya se anunció.
    //
    // `just_connected` sólo se limpia de la barra de direcciones: el éxito lo cuenta la propia
    // tarjeta («Buscando tus cuentas…» y después «Conectada · N cuentas»), y el aviso extra que
    // este parámetro alimentaba nunca tuvo ni texto ni estilo — decía lo mismo, dos veces.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const connected = params.get('just_connected');
        const failed = params.get('oauth_error');
        if (failed && params.get('oauth_reason') === CANCELLED) {
            // Canceló en la pantalla del proveedor: la tarjeta lo dice como en Meta, no como un error.
            setCancelledChannel(failed);
        } else if (failed) {
            setFailedChannel(failed);
            setFailedReason(params.get('oauth_reason'));
        }
        // Qué pasó al volver del proveedor (UD-23): la otra mitad del toque que cuenta `connect`.
        const returnEvent = connectReturnEvent({connected, failed, reason: params.get('oauth_reason')});
        if (returnEvent) ScreenTracker.recordEvent(returnEvent);
        if (connected || failed) {
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, []);

    /**
     * El estado de las conexiones. `withAccounts` agrega el padrón de cuentas, que es una consulta
     * cara: se pide al entrar y cuando una credencial termina de sincronizar, no en cada vuelta del
     * ciclo — eran hasta 37 pedidos de la cartera entera para responder «¿ya terminó?».
     */
    const refresh = useCallback(({withAccounts = false} = {}) => {
        const pending = [fetchConnections()];
        if (withAccounts) pending.push(loadCandidates());
        return Promise.all(pending).then(([response]) => {
            if (response?.status === 'OK') setConnections(response.data);
            return response;
        });
    }, [loadCandidates]);

    useEffect(() => {
        refresh({withAccounts: true});
    }, [refresh]);

    // Cuando deja de haber credenciales sincronizando, se trae el padrón una vez: ahí es cuando las
    // cuentas nuevas ya existen y el contador de la tarjeta tiene algo distinto que decir.
    //
    // `useLayoutEffect` y no `useEffect`: el render en que el sync termina todavía no tiene el
    // padrón nuevo en vuelo, así que la tarjeta leía «0 cuentas, nada cargando» y pintaba
    // «Conectada · sin cuentas publicitarias» durante un cuadro, con el banner diciendo
    // «Buscando…» (el flash que se vio en el video de Leo, card 2yINyKQv). Pedirlo antes del
    // pintado marca `loading.candidates` en el mismo cuadro.
    const syncing = anyCredentialSyncing(connections);
    const wasSyncing = useRef(false);
    useLayoutEffect(() => {
        if (wasSyncing.current && !syncing) loadCandidates();
        wasSyncing.current = syncing;
    }, [syncing, loadCandidates]);

    // Mientras el backend trae las cuentas de la credencial recién conectada, se vuelve a preguntar
    // cada tanto. `tick` avanza sólo, sin depender de que la respuesta cambie el estado: un
    // microcorte devuelve el mismo objeto, y si el ciclo dependiera de eso el paso se quedaría
    // congelado en «Buscando tus cuentas…» para siempre por un blip de red.
    //
    // El tope existe porque una espera sin final es peor que un error, y cuando se cumple la
    // pantalla LO DICE: dejar el spinner girando prometería algo que ya no va a pasar.
    useEffect(() => {
        if (!syncing || gaveUp) {
            pollStartedAt.current = null;
            return undefined;
        }
        if (pollStartedAt.current === null) pollStartedAt.current = Date.now();

        let alive = true;
        let round = 0;
        const tick = () => {
            if (!alive) return;
            if (Date.now() - pollStartedAt.current > MAX_POLL_MS) {
                setGaveUp(true);
                return;
            }
            round += 1;
            refresh({withAccounts: round % ACCOUNTS_EVERY === 0}).then(() => {
                if (alive) pollTimer.current = setTimeout(tick, POLL_MS);
            });
        };
        pollTimer.current = setTimeout(tick, POLL_MS);

        return () => {
            alive = false;
            clearTimeout(pollTimer.current);
        };
        // Deliberadamente NO depende de `connections`: el ciclo se sostiene solo y volver a armarlo
        // en cada respuesta reiniciaba el reloj del tope.
    }, [syncing, gaveUp, refresh]);

    const recordTap = channel => connectTapEvents(channel).forEach(event => ScreenTracker.recordEvent(event));

    const connect = channel => {
        // Meta no pasa por acá al tocar «Conectar» (su botón abre el popup solo): lo cuenta la tarjeta.
        if (channel !== FACEBOOK_CHANNEL) recordTap(channel);
        setFailedChannel(null);
        setFailedReason(null);
        setCancelledChannel(null);
        setOauthReturn('/onboarding/plataformas');
        // Una conexión nueva: el «Reconectar» de una cuenta que se abandonó no puede colarse acá.
        forgetReconnect();
        if (channel === GOOGLE_CHANNEL) {
            fetchAuthUrlFor('').then(data => {
                if (data?.auth_url) {
                    window.location = data.auth_url;
                    return;
                }
                // La nota se deja ANTES de pedir la dirección de autorización; si el pedido falla,
                // el usuario se queda acá y esa nota sobreviviría quince minutos, desviando al túnel
                // la próxima conexión que hiciera por la pantalla clásica.
                clearOauthReturn();
                // Sin este evento, el toque quedaba sin vuelta y se leía como «se perdió en Google».
                ScreenTracker.recordEvent(connectErrorEvent(channel, NO_AUTH_URL));
                setFailedChannel(channel);
                setFailedReason(isNetworkError(data) ? 'NETWORK_ERROR' : null);
            });
            return;
        }
        if (channel === TIKTOK_CHANNEL) {
            const redirect = encodeURIComponent(`${APP_URL}connect-tiktok`);
            window.location.href = `https://business-api.tiktok.com/portal/auth?app_id=${TIKTOK_APP_ID}` +
                `&state=onboarding&redirect_uri=${redirect}`;
            return;
        }
        if (channel === LINKEDIN_CHANNEL) {
            // Conexión NUEVA: una reconexión abandonada en Conexiones no puede colarse acá.
            clearPendingLinkedinReconnect();
            window.location.href = LINKEDIN_AUTHORIZATION_URL;
        }
        // Meta autoriza en un popup: lo dispara la propia tarjeta.
    };

    const accounts = server.candidates?.accounts || [];
    /**
     * Las tarjetas cuentan lo mismo que el número grande: las no cerradas y todavía sin línea (D7 y
     * review C-tunel-04). Si no, «Conectada · 1.110 cuentas» convivía con «Encontramos 143 cuentas
     * activas» y la suma no cerraba.
     *
     * Con una excepción: si TODAS las cuentas abiertas de la plataforma ya tienen línea, la tarjeta
     * dice cuántas tiene. Contar cero ahí la haría afirmar «Conectada · sin cuentas publicitarias»
     * sobre una conexión que sí las tiene, sólo que ya en seguimiento.
     */
    const accountsOf = channel => {
        const open = accounts.filter(account => account.channel === channel && !isClosedAccount(account));
        const offerable = open.filter(account => !isCoveredAccount(account)).length;
        return offerable || open.length;
    };
    /**
     * Si la tarjeta ya puede afirmar su número.
     *
     * Un cero con el padrón EN VUELO no es un cero: es el padrón viejo, de antes de que la cuenta
     * recién conectada terminara de sincronizar. Sin esto la tarjeta decía «Conectada · sin cuentas
     * publicitarias» mientras el banner de arriba decía «Buscando…» (card 2yINyKQv). Con cuentas se
     * sigue mostrando el número mientras se recarga: si no, cada vuelta del poll la haría parpadear.
     */
    const accountsKnownFor = channel => Boolean(server.candidates)
        && (accountsOf(channel) > 0 || !loading.candidates);
    const hasAnyConnection = PLATFORMS.some(platform => isConnected(connections, platform.channel));
    // Una tarjeta conectada no ofrece botón de conectar, así que con las cuatro conectadas
    // «Conectar otra plataforma antes» scrolleaba a un grid sin nada que tocar: un botón muerto.
    const hasPendingPlatform = PLATFORMS.some(platform => !isConnected(connections, platform.channel));
    // Si ya sabemos QUÉ hay conectado. `hasAnyConnection` vale `false` mientras `connections` es
    // nulo, que es lo mismo que diría una agencia sin nada: no alcanza para afirmar.
    const connectionsKnown = connections !== null;
    // Sabemos que no hay ninguna conectada, así que el pie puede decir por qué no se puede avanzar.
    const muestraMotivo = connectionsKnown && !hasAnyConnection;
    // En el celular y sin nada conectado, una línea debajo de las tarjetas dice que el link para seguir
    // ya está en su correo (UD-23, versión del 01/11). Con una plataforma conectada no hace falta.
    const avisaSeguirDespues = muestraMotivo && isPhoneViewport();
    const {open: discoveredTotal, closed: closedTotal} = accountCountsOf(accounts);
    // Qué decir sobre el conteo lo decide `platformsResult.js` — ver ahí por qué un cero puede no
    // ser un cero, y por qué el número grande no cuenta las cerradas.
    const resultState = resultStateOf({
        discoveredTotal,
        syncing,
        loadingCandidates: loading.candidates,
        candidatesArrived: Boolean(server.candidates),
    });

    return (
        <div className="ob-step-body">
            <header className="ob-head">
                <h1 {...CLARITY_UNMASK}>{t('onboarding.platforms.title')}</h1>
                <p {...CLARITY_UNMASK}>{t('onboarding.platforms.subtitle')}</p>
            </header>

            {hasAnyConnection && (
                <section className={resultClassName(resultState)} aria-live="polite">
                    <h2>
                        {t(resultTitleKey(resultState), {count: discoveredTotal})}
                        {/* Las cerradas, dichas aparte y más bajo: no se pueden monitorear, pero
                            son parte del total que el paso 3 va a desglosar. Sin decirlas, el
                            «+ 967 cerradas» del paso siguiente aparecía de la nada. */}
                        {showsClosedCount(resultState) && closedTotal > 0 && (
                            <span className="ob-result__closed">
                                {' '}{t('onboarding.platforms.found_closed', {count: closedTotal})}
                            </span>
                        )}
                    </h2>
                    <p {...CLARITY_UNMASK}>{t(resultHintKey(resultState))}</p>
                </section>
            )}

            <div className="ob-platforms" ref={platformsRef}>
                {PLATFORMS.map(platform => (
                    <PlatformCard
                        key={platform.channel}
                        channel={platform.channel}
                        hintKey={platform.hintKey}
                        connected={isConnected(connections, platform.channel)}
                        syncing={!gaveUp && isChannelSyncing(connections, platform.channel)}
                        stalled={gaveUp && isChannelSyncing(connections, platform.channel)}
                        accountCount={accountsOf(platform.channel)}
                        accountsKnown={accountsKnownFor(platform.channel)}
                        failed={failedChannel === platform.channel}
                        failedReason={failedChannel === platform.channel ? failedReason : null}
                        cancelled={cancelledChannel === platform.channel}
                        onConnect={() => connect(platform.channel)}
                        onRetry={() => connect(platform.channel)}
                        onDismissError={() => {
                            setFailedChannel(null);
                            setFailedReason(null);
                            setCancelledChannel(null);
                        }}
                        onCancelled={() => {
                            ScreenTracker.recordEvent(connectCancelEvent(platform.channel));
                            setCancelledChannel(platform.channel);
                        }}
                        onUnavailable={() => {
                            ScreenTracker.recordEvent(connectErrorEvent(platform.channel, FACEBOOK_SDK_NOT_LOADED));
                            setFailedChannel(platform.channel);
                            setFailedReason(FACEBOOK_SDK_NOT_LOADED);
                        }}
                        onTap={() => recordTap(platform.channel)}
                    />
                ))}
                <LaterPlatformsCard/>
            </div>

            {avisaSeguirDespues && <MobileLaterNote/>}

            {/* El avance vive ABAJO A LA DERECHA, como en el resto del túnel.
                Estaba en el medio de la pantalla, al lado de «Encontramos N cuentas», y el paso
                terminaba sin nada debajo: Pablo llegó al final, no encontró cómo seguir y fue a
                buscar un skip que tampoco había (card tPLkTNMI). El pie está SIEMPRE, incluso antes
                de conectar nada, porque lo que faltaba no era sólo la ubicación del botón sino
                saber que la salida existe y dónde está. */}
            <footer className="ob-foot">
                {hasAnyConnection && !hasPendingPlatform ? (
                    /* Las cuatro conectadas: no hay otra que sumar. El `<span/>` sostiene el
                       `space-between` para que el primario siga a la derecha. */
                    <span/>
                ) : hasAnyConnection ? (
                    /* Al GRID de plataformas, que es lo que el botón nombra. Llevaba a
                       `window.scrollTo({top: 0})`, y ahí no hay ninguna. Encima el que scrollea no
                       es la ventana sino `.ob-tunnel` —`position: fixed` con `overflow-y: auto`—,
                       de modo que `window.scrollTo` no movía nada: en una pantalla donde entra
                       todo, el botón no hacía absolutamente nada al tocarlo.
                       Sólo con alguna conectada: decir «otra» sin ninguna no significa nada. */
                    <button type="button" className="ob-btn -ghost"
                            onClick={() => platformsRef.current?.scrollIntoView(
                                {behavior: 'smooth', block: 'center'})}>
                        {t('onboarding.platforms.connect_another')}
                    </button>
                ) : connectionsKnown ? (
                    /* Un botón gris y mudo es peor que no tenerlo: este texto ocupa el lugar del
                       secundario y dice POR QUÉ todavía no se puede avanzar.
                       El `id` lo ata al botón (`aria-describedby`): sin eso, para un lector de
                       pantalla son dos cosas sueltas y el motivo queda tan lejos del control como
                       lo estaba antes de bajarlo al pie. */
                    <p className="ob-note" id={MOTIVO_BLOQUEO_ID}>
                        {t('onboarding.platforms.next_blocked')}
                    </p>
                ) : (
                    /* Todavía no sabemos qué hay conectado, así que el pie no afirma nada.
                       Sin esta rama, durante los segundos que tarda `agency/connections/` —medidos
                       en 6,6 s con la agencia 130— la pantalla le decía «Conecta al menos una
                       plataforma para seguir» a una agencia con 1147 cuentas, y al mismo tiempo le
                       habilitaba «Siguiente», porque el botón mira el PADRÓN y la nota miraba las
                       CONEXIONES: dos fuentes que no llegan juntas (QA-plat-08). Si `connections`
                       nunca llega, el hueco se queda — que es lo honesto: no sabemos.
                       El `<span/>` mantiene los dos hijos del `space-between`; sin él el primario
                       se iría al borde IZQUIERDO. */
                    <span/>
                )}
                {/* Sin una sola cuenta no hay cartera que armar: el paso siguiente aterrizaría en
                    una lista vacía. Antes eso se resolvía escondiendo el botón; ahora se muestra
                    deshabilitado, que dice lo mismo sin borrar la salida de la pantalla. */}
                <button type="button" className="ob-btn -primary"
                        disabled={!canBuildPortfolio(resultState)}
                        aria-describedby={muestraMotivo ? MOTIVO_BLOQUEO_ID : undefined}
                        onClick={() => navigate('/onboarding/clientes')}>
                    {t('onboarding.platforms.build_portfolio')}
                </button>
            </footer>
        </div>
    );
}

function isConnected(connections, channel) {
    return credentialsOf(connections, channel).length > 0;
}

function isChannelSyncing(connections, channel) {
    return credentialsOf(connections, channel).some(credential => credential.is_syncing);
}

function anyCredentialSyncing(connections) {
    return PLATFORM_CHANNELS.some(channel => isChannelSyncing(connections, channel));
}
